import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { RRITag } from '../../../tags/interfaces/tag.interface';
import { RRITagValueFilter } from '../../interfaces/tag-filter-value.interface';
import { RRIInputOption } from '../../../form-inputs/interfaces/input-option.interface';
import { RRIInput } from '../../../form-inputs/interfaces/input-settings.interface';
import { RRIFilter } from '../../../common/interfaces/filters/filter.interface';
import { RRIFilterOption } from '../../../common/interfaces/filters/filter-options.interface';
import { RRIFilterOutput } from '../../../common/interfaces/filters/filter-output.interface';
import { RR_FILTER_TYPE } from '../../../common/enum/filters/filter-type.enum';
import { RR_GRAY_650 } from '../../../common/constants/colors';

@Component({
  selector: 'rr-tags-filter',
  templateUrl: './tags-filter.component.html',
  styleUrls: ['./tags-filter.component.scss'],
})
export class TagsFilterComponent<T extends RRITag>
  implements OnInit, AfterViewInit, OnChanges
{
  public dropdownModel: RRIFilter<RRIFilterOption<T>, T>;
  public dropDownSettings: RRIInput<RRIInputOption<string>>;
  public textFieldModel: RRIFilter<string, string>;
  public textFieldSettings: RRIInput<string>;
  public outputTags: RRITagValueFilter[];
  public displayTags: RRITagValueFilter[];
  public isTagValid: boolean;
  public options: RRIInputOption<string>[];

  @Input() model: RRIFilter<RRITagValueFilter[], T>;
  @Input() tags: RRIFilterOption<T>[];
  @Input() resetFilter: boolean;
  @Output() changedFilter: EventEmitter<RRIFilterOutput<RRITagValueFilter[]>> =
    new EventEmitter();
  @Output() hasBeenReseted: EventEmitter<void> = new EventEmitter();

  constructor() {
    this.isTagValid = false;
    this.options = [];
    this.outputTags = [];
    this.displayTags = [];

    this.dropdownModel = {
      type: RR_FILTER_TYPE.DROPDOWN_SELECT,
      settings: {
        invalid: false,
        tooltip: '',
        title: '',
      },
      filterBy: {
        fieldName: 'tag',
        value: null,
        defaultValue: undefined,
      },
    };

    this.textFieldModel = {
      type: RR_FILTER_TYPE.TEXT_SEARCH,
      settings: {
        invalid: false,
        tooltip:
          'Value to set to the tag. For filtering tags with any value set *.',
        title: '',
      },
      filterBy: {
        fieldName: 'value',
        value: '',
        defaultValue: '',
      },
    };
  }

  ngOnInit(): void {
    this.setDropDownSettings();
  }

  ngAfterViewInit(): void {
    this.setDropDownSettings();
    if (this.tags && this.tags.length) {
      this.options = this.tags.map((t) => ({
        id: t.value.id,
        value: t.value.name,
      }));
    }

    if (
      this.model &&
      this.model.filterBy &&
      (this.model.filterBy.value ||
        this.outputTags !== this.model.filterBy.value)
    ) {
      this.outputTags = this.model.filterBy.value || [];
      this.displayTags = this.model.filterBy.value
        ? this.model.filterBy.value.map((t) => ({
            ...t,
            name: t.value ? `${t.name}: ${t.value}` : `${t.name}`,
          }))
        : [];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tags'] && changes['tags'].currentValue) {
      this.options = this.tags.map((t) => ({
        id: t.value.id,
        value: t.value.name,
      }));
    }

    if (
      changes['resetFilter'] &&
      changes['resetFilter'].currentValue !==
        changes['resetFilter'].previousValue &&
      changes['resetFilter'].currentValue
    ) {
      this.model.filterBy.value = this.model.filterBy.defaultValue;
      this.setTextInputDefaultValues();
      this.setDropdownDefaultValue();

      this.outputTags = this.model.filterBy.value
        ? this.model.filterBy.value
        : [];
      this.displayTags = this.model.filterBy.value
        ? this.model.filterBy.value.map((tag) => ({
            ...tag,
            name: tag.value ? `${tag.name}: ${tag.value}` : `${tag.name}`,
          }))
        : [];

      this.hasBeenReseted.emit();
    }
  }

  public changeSelectedFilter(event: RRIInputOption<string>): void {
    if (!event || event.id === '' || !(this.tags && this.tags.length)) {
      this.isTagValid = false;
      return;
    }

    const tag = this.tags.find((t) => t.value.id === event.id);
    if (this.isAnOption(tag)) {
      this.isTagValid = true;
      this.dropdownModel.filterBy.value = Object.assign({}, tag);
    } else {
      this.setDropdownDefaultValue();
    }
  }

  public onClearInput(_event: any): void {
    this.setDropdownDefaultValue();
  }

  public addTagToFilter(): void {
    const tagId = this.dropdownModel.filterBy.value.id;
    const tag = this.tags.map((t) => t.value).find((t) => t.id === tagId);
    const value = this.textFieldModel.filterBy.value;

    this.displayTags.push({
      ...tag,
      name: value ? `${tag.name}: ${value}` : `${tag.name}`,
      value: value,
    });

    this.outputTags.push({
      ...tag,
      value: value,
    });

    this.setDropdownDefaultValue();
    this.setTextInputDefaultValues();
    this.changedFilter.emit({
      type: RR_FILTER_TYPE.TAGS,
      filterBy: {
        fieldName: this.model.filterBy.fieldName,
        value: this.outputTags,
        defaultValue: this.model.filterBy.defaultValue,
      },
    });
  }

  public removeTagFromFilter(tag: RRITag & { value?: string }): void {
    this.outputTags = this.outputTags.filter(
      (t) => t.id !== tag.id || (t.id === tag.id && t.value !== tag.value),
    );
    this.displayTags = this.displayTags.filter(
      (t) => t.id !== tag.id || (t.id === tag.id && t.value !== tag.value),
    );

    this.changedFilter.emit({
      type: RR_FILTER_TYPE.TAGS,
      filterBy: {
        fieldName: this.model.filterBy.fieldName,
        value: this.outputTags,
        defaultValue: this.model.filterBy.defaultValue,
      },
    });
  }

  public setTextInputDefaultValues(): void {
    this.textFieldModel.filterBy.value = '';
  }

  public changeInputFilter(): void {}

  public getTagColor(id: string | number): string {
    const tag = this.tags.find((t) => t.value.id === id);
    return tag ? tag.value.color || RR_GRAY_650 : RR_GRAY_650;
  }

  private setDropdownDefaultValue(): void {
    this.isTagValid = false;
    this.dropdownModel.filterBy.value = {
      id: '',
      value: null,
    };
  }

  private isAnOption(item: RRIFilterOption<RRITag>): boolean {
    return Object.assign(
      {},
      this.tags.some(
        (option) =>
          option.id === item.id &&
          item.value &&
          option.value &&
          option.value.id === item.value.id,
      ),
    );
  }

  private setDropDownSettings(): void {
    this.dropDownSettings = {
      fieldName: this.dropdownModel?.filterBy?.fieldName ?? '',
      label: this.model?.settings?.title ?? '',
      placeholder: this.dropdownModel?.settings?.title ?? '',
      tooltip: this.model?.settings?.tooltip ?? '',
      defaultValue: null,
    };

    this.textFieldSettings = {
      fieldName: this.textFieldModel?.filterBy?.fieldName ?? '',
      label: '',
      placeholder: this.textFieldModel?.settings?.title ?? '',
      tooltip: this.textFieldModel?.settings?.tooltip ?? '',
      defaultValue: null,
    };
  }
}
